// src/components/common/forms/EditableField.tsx
import React from 'react';
import { Edit3 } from 'lucide-react';
import { FieldConfig, FormValue } from '../hooks/useEntityForm';

interface EditableFieldProps {
  field: FieldConfig;
  value: FormValue;
  isEditing: boolean;
  onStartEdit: () => void;
  onStopEdit: () => void;
  onChange: (value: FormValue) => void;
  error?: string;
  className?: string;
  renderValue?: (value: FormValue) => string;
}

export const EditableField: React.FC<EditableFieldProps> = ({
  field,
  value,
  isEditing,
  onStartEdit,
  onStopEdit,
  onChange,
  error,
  className = '',
  renderValue,
}) => {
  // Utiliser field.render si disponible, sinon renderValue (prop), sinon valeur brute
  const displayValue = field.render
    ? field.render(value, {})
    : renderValue
      ? renderValue(value)
      : (value || 'Non défini');

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && field.type !== 'textarea') {
      onStopEdit();
    } else if (e.key === 'Escape') {
      onStopEdit();
    }
  };

  if (isEditing) {
    return (
      <div className={`group ${className}`}>
        <label className="form-label">
          {field.label}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {field.type === 'select' ? (
          <select
            value={value || ''}
            onChange={(e) => {
              const val = e.target.value;
              if (!val) {
                onChange(null);
                return;
              }
              // Convertir en number si toutes les options ont des valeurs numériques
              const numVal = Number(val);
              onChange(isNaN(numVal) ? val : numVal);
            }}
            onBlur={onStopEdit}
            onKeyDown={handleKeyDown}
            autoFocus
            className={`form-input ${error ? 'border-red-300' : ''}`}
            disabled={field.disabled}
          >
            <option value="">Sélectionner...</option>
            {field.options?.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        ) : field.type === 'textarea' ? (
          <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onStopEdit}
            autoFocus
            rows={3}
            maxLength={field.maxLength}
            placeholder={field.placeholder}
            className={`form-input ${error ? 'border-red-300' : ''}`}
            disabled={field.disabled}
          />
        ) : field.type === 'checkbox' ? (
          <input
            type="checkbox"
            checked={value || false}
            onChange={(e) => onChange(e.target.checked)}
            onBlur={onStopEdit}
            autoFocus
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            disabled={field.disabled}
          />
        ) : (
          <input
            type={field.type}
            value={value || ''}
            onChange={(e) => onChange(field.type === 'number' ? Number(e.target.value) : e.target.value)}
            onBlur={onStopEdit}
            onKeyDown={handleKeyDown}
            autoFocus
            maxLength={field.maxLength}
            placeholder={field.placeholder}
            className={`form-input ${error ? 'border-red-300' : ''}`}
            disabled={field.disabled}
          />
        )}
        {error && <p className="form-error">{error}</p>}
      </div>
    );
  }

  return (
    <div
      className={`editable-field ${className}`}
      onDoubleClick={field.disabled ? undefined : onStartEdit}
      style={{ cursor: field.disabled ? 'not-allowed' : 'pointer' }}
    >
      <label className="form-label">
        {field.label}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className={`flex items-center justify-between ${error ? 'border border-red-300 rounded px-2 py-1' : ''}`}>
        <span className={`text-gray-900 ${field.disabled ? 'opacity-50' : ''}`}>
          {displayValue}
        </span>
        {!field.disabled && <Edit3 className="h-4 w-4 text-gray-400 edit-icon" />}
      </div>
      {error && <p className="form-error">{error}</p>}
    </div>
  );
};